import React, { useCallback, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { CatalogItemType } from '../../../types/catalogItem';

interface ManageSupplierNamesModalProps {
  open: boolean;
  onClose: () => void;
  itemCode: string;
  itemName: string;
  currentAliases: Array<string>;
  type: CatalogItemType;
  onSave: (aliases: Array<string>) => void;
}

export default function ManageSupplierNamesModal({
  open,
  onClose,
  itemCode,
  itemName,
  currentAliases,
  type,
  onSave,
}: ManageSupplierNamesModalProps) {
  const [aliases, setAliases] = useState<Array<string>>([]);
  const [newAlias, setNewAlias] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Initialize aliases when modal opens
  useEffect(() => {
    if (open) {
      setAliases([...currentAliases]);
      setNewAlias('');
    }
  }, [open, currentAliases]);

  const handleAddNew = useCallback(() => {
    if (newAlias.trim()) {
      setAliases((prev) => [...prev, newAlias.trim()]);
      setNewAlias('');
    }
  }, [newAlias]);

  const handleRemove = useCallback((index: number) => {
    setAliases((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave(aliases);
      onClose();
    } catch (error) {
      // Error is handled by mutation's onError handler
      // Keep modal open so user can retry
    } finally {
      setIsSaving(false);
    }
  }, [aliases, onSave, onClose]);

  const handleClose = useCallback(() => {
    if (!isSaving) {
      setAliases([...currentAliases]);
      setNewAlias('');
      onClose();
    }
  }, [isSaving, currentAliases, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Manage Supplier Names - {itemName}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Add or remove supplier names (aliases) for this {type}. These names help match supplier data to this item.
            </Typography>

            {aliases.length > 0 && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {aliases.map((alias, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="body2">{alias}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(index)}
                      disabled={isSaving}
                      sx={{ color: 'error.main' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}

            {aliases.length === 0 && (
              <Box
                sx={{
                  p: 2,
                  textAlign: 'center',
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No supplier names added yet
                </Typography>
              </Box>
            )}

            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter supplier name"
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddNew();
                  }
                }}
                disabled={isSaving}
              />
              <Button
                sx={{ paddingRight: 0 }}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                disabled={!newAlias.trim() || isSaving}
              ></Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
